import React, { useEffect, useState, useRef } from "react";
import { Card, Col, Row, Table, Container, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill";
import 'react-quill/dist/quill.snow.css';
import { BsX } from 'react-icons/bs';
import Breadcrumbs from "../../../shared/components/Breadcrumbs";
import { DEFAULTGROUPS } from "../../../shared/constants/Constants";
import { loadChatGroupDataRequest } from "../redux/Actions";
import DefaultButton from "../../../shared/components/DefaultButton";
import FloatingChatWindow from "./FloatingChatWindow";
import { loadActiveEmployeeDataRequest } from "../../employees/redux/Actions";
import {
  sendMessage,
  listenToMessages,
  resetUnreadCount,
  uploadImageAndSendMessage,
  loadOlderMessages
} from '../../../firebase/chatService';
import { authenticateWithFirebase } from '../../../firebase/firebaseAuth'; 
import ImageModal from "./ImageModal";

const DefaultGroups = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const employee = JSON.parse(localStorage.getItem("employee"));
    const fullName = `${employee.first_name} ${employee.last_name}`;
    let [selectedRow, setSelectedRow] = useState({});
    const { chatgroup } = useSelector((state) => state.chatgroup);
    const { active_employees = [] } = useSelector((state) => state.employee);
    const [messageInput, setMessageInput] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [replyingTo, setReplyingTo] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    console.log("Chat Group Data:", employee);
    let [selectedForm, setSelectedForm] = useState(null);
    const [viewerImage, setViewerImage] = useState(null);

    // Lazy loading states
    const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(true);
    const [allMessages, setAllMessages] = useState([]);
    const [initialLoad, setInitialLoad] = useState(true);

    // Reference for the bottom of the message list
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const unsubscribeMessages = useRef(null);
    const floatingChatRef = useRef(null);
    const previousScrollHeight = useRef(0);

    // Scroll to the bottom of the message list
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "auto" }); // Instantly scroll to the bottom
        }
    };

    // Scroll to bottom only on initial load, not when loading older messages
    useEffect(() => {
        if (initialLoad && allMessages.length > 0) {
            scrollToBottom();
            setInitialLoad(false);
        }
    }, [allMessages, initialLoad]);
    
    // Authenticate with Firebase on mount
    useEffect(() => {
        const authenticate = async () => {
            try {
                await authenticateWithFirebase();
                setIsAuthenticated(true);
                console.log('Firebase authentication successful for DefaultGroups');
            } catch (error) {
                console.error('Failed to authenticate with Firebase:', error);
            }
        };
        authenticate();
    }, []);

    // Listen to messages in selected chat using Firebase
    useEffect(() => {
        if (selectedRow.id && isAuthenticated) {
            console.log('📩 Opening chat with ID:', selectedRow.id);

            // Reset states for new chat
            setAllMessages([]);
            setHasMoreMessages(true);
            setInitialLoad(true);

            // Clean up previous listener
            if (unsubscribeMessages.current) {
                unsubscribeMessages.current();
            }

            // Listen to messages from Firebase (gets last 50 messages)
            unsubscribeMessages.current = listenToMessages(selectedRow.id, (msgs) => {
                setAllMessages(msgs); // Update allMessages with real-time messages
            });

            // Reset unread count when chat is opened
            if (employee.employee_id) {
                const userId = employee.employee_id.toString();
                resetUnreadCount(selectedRow.id, userId);
            }
        }

        return () => {
            if (unsubscribeMessages.current) {
                unsubscribeMessages.current();
            }
        };
    }, [selectedRow.id, isAuthenticated, employee.employee_id]);

    useEffect(() => {
        // Load active employees for chat
        dispatch(loadActiveEmployeeDataRequest());

        return () => {
            // Clean up Firebase listeners on unmount
            if (unsubscribeMessages.current) {
                unsubscribeMessages.current();
            }
        };
    }, [dispatch]);

    // Function to load older messages
    const loadMoreMessages = async () => {
        if (loadingOlderMessages || !hasMoreMessages || allMessages.length === 0) {
            return;
        }

        setLoadingOlderMessages(true);

        try {
            const oldestMessage = allMessages[0];
            const oldestTimestamp = oldestMessage.timestamp;

            console.log('📜 Loading older messages before timestamp:', oldestTimestamp);

            // Load older messages
            const olderMessages = await loadOlderMessages(selectedRow.id, oldestTimestamp, 30);

            console.log('📜 Loaded older messages:', olderMessages.length);

            if (olderMessages.length === 0) {
                setHasMoreMessages(false);
                console.log('📜 No more older messages available');
            } else {
                // Store current scroll position
                const container = messagesContainerRef.current;
                const currentScrollHeight = container?.scrollHeight || 0;
                previousScrollHeight.current = currentScrollHeight;

                // Merge older messages with existing ones (older messages first)
                setAllMessages(prevMessages => {
                    // Remove duplicates by message ID
                    const existingIds = new Set(prevMessages.map(m => m.id));
                    const uniqueOlderMessages = olderMessages.filter(m => !existingIds.has(m.id));
                    return [...uniqueOlderMessages, ...prevMessages];
                });

                // Restore scroll position after new messages are rendered
                setTimeout(() => {
                    if (container) {
                        const newScrollHeight = container.scrollHeight;
                        const heightDifference = newScrollHeight - currentScrollHeight;
                        container.scrollTop = heightDifference;
                    }
                }, 0);
            }
        } catch (error) {
            console.error('Error loading older messages:', error);
        } finally {
            setLoadingOlderMessages(false);
        }
    };

    // Handle scroll event to detect when user scrolls to top
    const handleScroll = () => {
        const container = messagesContainerRef.current;
        if (!container || loadingOlderMessages || !hasMoreMessages) return;

        // Check if user scrolled near the top (within 100px)
        if (container.scrollTop < 100 && allMessages.length > 0) {
            console.log('📜 User scrolled to top, loading older messages...');
            loadMoreMessages();
        }
    };
    

    // Configure ReactQuill modules as needed
    const modules = {
        toolbar: [
            ['image'], // Only include the image option in the toolbar
        ],
    };
   
    useEffect(() => {
        if (employee.employee_id === '700006' || employee.employee_id === '135205') {
            dispatch(loadChatGroupDataRequest("auto"));
        } else {
            dispatch(loadChatGroupDataRequest('auto_default'));
        }
    }, []);

    const getInitials = (name) => {
        const words = name.split(' ');
        return words.length > 1
            ? words[0][0] + words[1][0]
            : words[0].slice(0, 2);
    };

    const getColorFromName = (name) => {
        const colors = ['#3498db', '#e67e22', '#1abc9c', '#9b59b6', '#e74c3c', '#f39c12'];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    };

    const htmlToPlainText = (html) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
    
        // Replace <br> tags with \n
        let plainText = tempDiv.innerHTML.replace(/<br\s*\/?>/gi, "\n");
    
        // Replace block-level tags (like <p>) with \n
        plainText = plainText.replace(/<\/?p[^>]*>/gi, "\n");
    
        // Remove any remaining HTML tags
        plainText = plainText.replace(/<[^>]+>/g, "");
    
        // Normalize multiple consecutive line breaks to a single line break
        return plainText.replace(/(\r\n|\n|\r){2,}/g, "\n\n").trim();
    };

    const handleSendMessage = async (event) => {
        event.preventDefault();
        if (!messageInput.trim() || !selectedRow) return; // Prevent sending empty messages

        try {
            const normalizedMessage = htmlToPlainText(messageInput);

            // Prepare replyTo payload if replying to a message
            const replyToPayload = replyingTo ? {
                id: replyingTo.id,
                content: replyingTo.content,
                contentType: replyingTo.contentType || 'text',
                senderName: replyingTo.senderName
            } : null;

            // Send message via Firebase
            await sendMessage(
                selectedRow.id,
                employee.employee_id,  // Use employee_id as senderId
                fullName,
                normalizedMessage,
                'text',
                replyToPayload
            );

            console.log("Message sent successfully via Firebase");

            setMessageInput("");
            setReplyingTo(null); // Clear reply state after sending
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message. Please try again.');
        }
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Validate if it's an image
        if (!file.type.startsWith('image/')) {
            alert('Please select an image file');
            return;
        }

        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        setSelectedImage(file);
        setImagePreviewUrl(previewUrl);
    };

    const handleImageSend = async () => {
        if (!selectedImage || !selectedRow.id) return;

        setUploading(true);
        try {
            // Prepare replyTo payload if replying to a message
            const replyToPayload = replyingTo ? {
                id: replyingTo.id,
                content: replyingTo.content,
                contentType: replyingTo.contentType || 'text',
                senderName: replyingTo.senderName
            } : null;

            // Upload image via Firebase
            await uploadImageAndSendMessage(
                selectedRow.id,
                employee.employee_id,
                fullName,
                selectedImage,
                replyToPayload
            );

            console.log('Image sent successfully via Firebase');

            // Clear image selection and reply state
            setSelectedImage(null);
            setImagePreviewUrl(null);
            setReplyingTo(null);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to send image. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const handleCancelImage = () => {
        if (imagePreviewUrl) {
            URL.revokeObjectURL(imagePreviewUrl);
        }
        setSelectedImage(null);
        setImagePreviewUrl(null);
    };

    const handleOpenOneOnOneChat = async (senderId, senderName) => {
        // Find the user in active_employees list
        const user = active_employees.find(emp => emp.employee_id === senderId);

        if (user && floatingChatRef.current) {
            try {
                await floatingChatRef.current.openChatWithUser(user);
            } catch (error) {
                console.error('Error opening one-on-one chat:', error);
            }
        } else if (!user) {
            console.warn('User not found in active employees:', senderId, senderName);
            // You could still try to create a basic user object if needed
            // For now, we'll just log a warning
        }
    };

    const makeLinksClickable = (text) => {
        const urlRegex = /(https?:\/\/[^\s<]+)/g; // Regex to detect URLs, excluding `<br>` and other tags
        return text
            .replace(urlRegex, (url) => {
                return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
            })
            .replace(/<br\s*\/?>/gi, "<br>"); // Ensure <br> tags are preserved as line breaks
    };

    const hasChatAccess = (groupName) => {
        if (employee.role === 'admin') {
            return true;
        }

        if (employee.employee_id === '700006') {
            return true;
        }

        // Check if the group exists in the chat_access array
        const groupAccess = employee.chat_access?.find((access) => Object.keys(access)[0] === groupName);

        // If the group exists in chat_access, check if it has true permission
        if (groupAccess) {
            return groupAccess[groupName] === true;
        }

        // If the group doesn't exist in chat_access, allow access by default
        return true;
    };

    const getFileNameFromFirebaseUrl = (url) => {
        try {          
          const encodedPath = url.split('/o/')[1].split('?')[0];   
          const decodedPath = decodeURIComponent(encodedPath); 
          const fileName = decodedPath.split('/').pop();
          return fileName;
        } catch (error) {
          console.error("Invalid URL format:", error);
          return null;
        }
      };

    return (
        <>
            <Row>
                <Col className="gx-0">
                    <Breadcrumbs
                        children={[
                            { href: '/', label: DEFAULTGROUPS },
                        ]}
                    />
                </Col>
            </Row>

            <Container fluid className="mt-3">
                <Row>
                    <Col md={12}>
                        <div style={{
                            display: 'flex',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            padding: '0px',
                            alignItems: 'flex-start',
                            height: '600px',
                            gap: 0,
                            overflow: 'hidden',
                        }}>
                            {/* Table on the left */}
                            <div style={{ minWidth: '250px', width:'25%', height: '100%', padding: 0, margin: 0 }}>
                                <Card className="w-100" style={{ height: '100%', padding: 0, margin: 0, borderRadius: 0, border: "none" }}>
                                <div style={{ padding: '10px' }}>
                                        <input
                                            type="text"
                                            placeholder="Search groups..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                borderRadius: '4px',
                                                border: '1px solid #ccc',
                                            }}
                                        />
                                </div>
                                    <Table
                                        bordered={false}
                                        hover
                                        responsive
                                        style={{ marginBottom: 0, borderColor: 'transparent' }}
                                        
                                    >
                                        <tbody>
                                            {chatgroup?.length > 0 ? (
                                                chatgroup.filter(group =>group.chat_name.toLowerCase().includes(searchTerm.toLowerCase()))                                                
                                                .map((group, index) => {
                                                    const lastMessageDate = group.lastMessageTime
                                                                            ? new Date(group.lastMessageTime).toLocaleDateString('en-US', {
                                                                                year: 'numeric',
                                                                                month: 'short',
                                                                                day: 'numeric',
                                                                            })
                                                                            : ''; 
                                                return (                        
                                                    <tr
                                                    key={group.id || index}
                                                    onClick={() => {
                                                        setSelectedRow(group);
                                                        setReplyingTo(null); // Clear any active reply
                                                    }}
                                                    className="no-border"
                                                    style={{
                                                        cursor: 'pointer',
                                                        backgroundColor: selectedRow.id === group.id ? '#177978' : 'transparent',
                                                    }}
                                                >
                                                    <td style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%' 
                                                        ,color: selectedRow.id === group.id ? 'white' : '#333',
                                                    }}>
                                                        {/* Group Icon */}
                                                        <div
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '50%',
                                                                backgroundColor: getColorFromName(group.chat_name),
                                                                color: 'white',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontWeight: 'bold',
                                                                fontSize: '16px',
                                                                textTransform: 'uppercase',
                                                            }}
                                                        >
                                                            {getInitials(group.chat_name)}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', width: '280px' }}>                                                            
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: '#333' , marginTop: "5px", color: selectedRow.id === group.id ? 'white' : '#333'}}>
                                                                    {group.chat_name}
                                                                </span>
                                                                <span style={{ fontSize: '0.75rem', color: '#999', whiteSpace: 'nowrap' , marginTop: "5px", color: selectedRow.id === group.id ? 'white' : '#999'}}>
                                                                    {lastMessageDate}
                                                                </span>
                                                            </div>
                                                            <div dangerouslySetInnerHTML={{ __html: group.lastMessage }}
                                                                style={{
                                                                    fontSize: '0.85rem',
                                                                    color: selectedRow.id === group.id ? 'white' : '#555', 
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                }}
                                                            > 
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                                );})
                                            ) : (
                                                <tr>
                                                    <td className="text-center">No groups found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </Card>
                            </div>

                            {/* Message display area */}
                            <div style={{ width:'75%', height: '100%' }}>
                                <Card className="w-100" style={{ height: '100%', padding: 0 }}>                                     
                                    <div
                                        style={{
                                            position: 'sticky',
                                            top: 0,
                                            backgroundColor: '#f4f4f4',
                                            padding: '10px 20px',
                                            borderBottom: '1px solid #ddd',
                                            fontWeight: 'bold',
                                            fontSize: '1.2rem',
                                            zIndex: 1,
                                        }}
                                    >
                                        {selectedRow.chat_name || 'Select a Group'}
                                    </div>
                                    <Card.Body
                                        ref={messagesContainerRef}
                                        onScroll={handleScroll}
                                        style={{
                                            overflowY: 'auto',
                                            height: 'calc(100% - 100px)',
                                            padding: '20px'
                                        }}
                                    >
                                        {/* Loading indicator for older messages */}
                                        {loadingOlderMessages && (
                                            <div style={{ textAlign: 'center', padding: '10px', color: '#0a7674' }}>
                                                <i className="fas fa-spinner fa-spin"></i> Loading older messages...
                                            </div>
                                        )}

                                        {/* No more messages indicator */}
                                        {!hasMoreMessages && allMessages.length > 0 && (
                                            <div style={{ textAlign: 'center', padding: '10px', color: '#999', fontSize: '0.85rem' }}>
                                                No more messages
                                            </div>
                                        )}

                                        {allMessages && allMessages.length > 0 ? (
                                            (() => {
                                                // Group messages by date
                                                const groupedMessages = allMessages.reduce((acc, msg) => {
                                                    const date = new Date(msg.timestamp).toDateString();
                                                    if (!acc[date]) {
                                                        acc[date] = [];
                                                    }
                                                    acc[date].push(msg);
                                                    return acc;
                                                }, {});

                                                return Object.entries(groupedMessages).map(([date, msgs]) => (
                                                <div key={date} style={{ marginBottom: '20px' }}>
                                                    
                                                    <div 
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: '20px',
                                                            position: 'relative'
                                                        }}>
                                                        <hr className="date-divider-line" />
                                                        <span
                                                            style={{
                                                                backgroundColor: '#f4f4f4',
                                                                padding: '5px 15px',
                                                                borderRadius: '20px',
                                                                fontWeight: 'bold',
                                                                fontSize: '.75rem',
                                                                color: '#555',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                        {new Date(date).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'long',
                                                            day: 'numeric',
                                                        })}
                                                        </span>
                                                        <hr className="date-divider-line" />
                                                    </div>

                                                    {msgs.map((msg, idx) => (
                                                        <div
                                                            key={idx}
                                                            style={{
                                                                marginBottom: '5px',
                                                                paddingBottom: '5px'
                                                            }}
                                                            onContextMenu={(e) => {
                                                                e.preventDefault();
                                                                setReplyingTo({
                                                                    id: msg.id || idx,
                                                                    content: msg.content,
                                                                    contentType: msg.contentType || 'text',
                                                                    senderName: msg.senderName
                                                                });
                                                            }}
                                                        >
                                                            <div>
                                                                <strong>
                                                                    <span
                                                                        onClick={() => handleOpenOneOnOneChat(msg.senderId, msg.senderName)}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            color: '#0a7674',
                                                                            textDecoration: 'none'
                                                                        }}
                                                                        onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                                                        onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                                                                        title="Click to open private chat"
                                                                    >
                                                                        {msg.senderName}
                                                                    </span>
                                                                    {' @'}{msg.senderId}
                                                                    <span style={{ fontSize: '0.8rem', color: '#999', marginLeft: '10px' }}>
                                                                        {new Date(msg.timestamp).toLocaleTimeString()}
                                                                    </span>
                                                                </strong>
                                                            </div>

                                                            {/* Show replied message preview if this is a reply */}
                                                            {msg.replyTo && (
                                                                <div style={{
                                                                    backgroundColor: '#f0f0f0',
                                                                    borderLeft: '3px solid #0a7674',
                                                                    padding: '8px 12px',
                                                                    marginTop: '5px',
                                                                    marginBottom: '5px',
                                                                    borderRadius: '4px'
                                                                }}>
                                                                    <div style={{
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 'bold',
                                                                        color: '#0a7674',
                                                                        marginBottom: '2px'
                                                                    }}>
                                                                        {msg.replyTo.senderName}
                                                                    </div>
                                                                    <div style={{
                                                                        fontSize: '0.85rem',
                                                                        color: '#555',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {msg.replyTo.content}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {msg.contentType === "image" ? (
                                                                <img
                                                                    src={msg.contentUrl[0]['fileUrl']}
                                                                    alt="Message Content"
                                                                    onClick={() => setViewerImage(msg.contentUrl[0]['fileUrl'])}
                                                                    style={{
                                                                        width: '360px',
                                                                        maxWidth: '30%',
                                                                        height: 'auto',
                                                                        borderRadius: '5px',
                                                                        marginTop: '10px',
                                                                        cursor: 'pointer',        
                                                                        transition: 'opacity 0.2s',
                                                                    }}
                                                                    onMouseEnter={(e) => e.target.style.opacity = '0.85'}
                                                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                                                />
                                                            ) : msg.contentType === "file" ? (
                                                            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center' }}>
                                                                {/* File Icon */}
                                                                <i
                                                                    className="fas fa-file-alt"
                                                                    style={{
                                                                        fontSize: '1.2rem',
                                                                        color: '#007bff',
                                                                        marginRight: '8px',
                                                                    }}
                                                                ></i>

                                                                {/* File Name with Link */}
                                                                <a
                                                                    href={msg.contentUrl[0]['fileUrl']}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    style={{
                                                                        textDecoration: 'none',
                                                                        color: '#007bff',
                                                                        fontWeight: 'bold',
                                                                        wordBreak: 'break-word', // Handle long file names
                                                                    }}
                                                                >
                                                                    {getFileNameFromFirebaseUrl(msg.contentUrl[0]['fileUrl']) }                            
                                                                </a>
                                                            </div>
                                                            ) : (
                                                            <div dangerouslySetInnerHTML={{
                                                                    __html: makeLinksClickable(msg.content.replace(/\n/g, "<br>")),
                                                                }}
                                                                style={{
                                                                    whiteSpace: "pre-wrap", // Preserve spaces and line breaks
                                                                    wordBreak: "break-word", // Handle long URLs
                                                                }}
                                                            ></div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                ));
                                            })()
                                        ) : (
                                            <p>No messages to display</p>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </Card.Body>

                                    {/* Message Editor using ReactQuill */}
                                    {selectedRow.id && (
                                    <Card.Footer style={{ padding: '10px 20px', borderTop: '1px solid #ddd' }}>
                                        {/* Reply preview bar */}
                                        {replyingTo && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                backgroundColor: '#f0f0f0',
                                                borderLeft: '3px solid #0a7674',
                                                padding: '8px 12px',
                                                marginBottom: '10px',
                                                borderRadius: '4px',
                                                gap: '10px'
                                            }}>
                                                <div style={{ flexGrow: 1 }}>
                                                    <div style={{
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                        color: '#0a7674',
                                                        marginBottom: '2px'
                                                    }}>
                                                        Replying to {replyingTo.senderName}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '0.85rem',
                                                        color: '#555',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {replyingTo.content}
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    onClick={() => setReplyingTo(null)}
                                                    style={{ padding: '0', minWidth: 'auto' }}
                                                >
                                                    <BsX size={20} />
                                                </Button>
                                            </div>
                                        )}

                                        {/* Image preview */}
                                        {imagePreviewUrl && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                backgroundColor: '#f0f0f0',
                                                padding: '10px',
                                                marginBottom: '10px',
                                                borderRadius: '4px',
                                                gap: '10px'
                                            }}>
                                                <img
                                                    src={imagePreviewUrl}
                                                    alt="Preview"
                                                    style={{
                                                        maxWidth: '100px',
                                                        maxHeight: '100px',
                                                        borderRadius: '4px',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <div style={{ flexGrow: 1 }}>
                                                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                        {selectedImage?.name}
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: '#666' }}>
                                                        Ready to send
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={handleImageSend}
                                                    disabled={uploading}
                                                >
                                                    {uploading ? 'Sending...' : 'Send'}
                                                </Button>
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    onClick={handleCancelImage}
                                                    disabled={uploading}
                                                    style={{ padding: '0', minWidth: 'auto' }}
                                                >
                                                    <BsX size={20} />
                                                </Button>
                                            </div>
                                        )}

                                        <div style={{ display: 'flex', alignItems: 'flex-end',
                                           border: '1px solid #0a7674',
                                           borderRadius: '5px',
                                           backgroundColor: '#FFF',
                                           flexGrow: 1, // Allow the parent div to grow
                                            maxHeight: '350px', // Set a maximum height for the parent div
                                            overflowY: 'auto', // Prevent overflow when the content exceeds max height
                                         }}>
                                            {/* <ReactQuill
                                                value={messageInput}
                                                onChange={(content) => setMessageInput(content)}
                                                modules={modules}  
                                                readOnly={employee.role !== 'admin' || employee.chat_access[selectedRow.chat_name] === true}                                             
                                                style={{ flexGrow: 1,
                                                    maxHeight: '250px', 
                                                    overflowY: 'auto', 
                                                    backgroundColor: !(employee.role === 'admin' || employee.chat_access[selectedRow.chat_name] === true) ? '#f5f5f5' : 'white',
                                                 }}
                                            /> */}

                                            <textarea
                                                    value={messageInput}
                                                    onChange={(e) => setMessageInput(e.target.value)}
                                                    onPaste={(e) => {
                                                        e.preventDefault(); 
                                                        const pastedData = e.clipboardData.getData('Text'); // Get pasted text
                                                        setMessageInput((prev) => prev + pastedData); // Append pasted content
                                                    }}
                                                    placeholder="Type your message here..."
                                                    style={{
                                                        flexGrow: 1,
                                                        // maxHeight: '350px',
                                                        // overflowY: 'auto',
                                                        padding: '10px',
                                                        borderRadius: '5px',
                                                        border: '0px solid #ddd',
                                                        resize: 'none',
                                                        backgroundColor: !hasChatAccess(selectedRow.chat_name)? '#f5f5f5' : 'white',
                                                        color: '#333',
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                    }}
                                                    disabled={!hasChatAccess(selectedRow.chat_name)}
                                            />
                                            <div style={{ marginRight: '3px', height:'44px', display: 'flex', alignItems: 'start', gap: '5px' }}>
                                                <label htmlFor="image-upload" style={{ cursor: 'pointer' }}>
                                                    <i className="fas fa-image" style={{ fontSize: '1.5rem', color: '#0a7674' }} title="Upload image"></i>
                                                </label>
                                                <input
                                                    id="image-upload"
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={handleImageSelect}
                                                />
                                            </div>
     
                                            <DefaultButton
                                                buttonClass={`btn msg-btn ${
                                                    !hasChatAccess(selectedRow.chat_name) ? 'disabled' : ''
                                                }`}
                                                buttonTitle={<i className="fas fa-location-arrow"></i>}
                                                buttonType={"submit"}
                                                actionBlock={(event) => {
                                                    if (hasChatAccess(selectedRow.chat_name)) {
                                                        handleSendMessage(event);
                                                    }
                                                }}
                                            />
                                        </div>
                                    </Card.Footer>
                                    )}
                                </Card>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Floating Chat Window for One-on-One Conversations */}
            <FloatingChatWindow
                ref={floatingChatRef}
                currentUser={{
                    id: employee.id,
                    first_name: employee.first_name,
                    last_name: employee.last_name,
                    employee_id: employee.employee_id,
                    role: employee.role
                }}
                allUsers={active_employees}
            />
            {viewerImage && (
                <ImageModal
                    imageUrl={viewerImage}
                    onClose={() => setViewerImage(null)}
                />
            )}
        </>
    );
};

export default DefaultGroups;
